import type { Editor } from "@tiptap/react"
import { useCallback, useEffect, useRef, useState } from "react"
import { parseEmbedInput } from "./embed-iframe-extension"

export function useEmbedComposer(editor: Editor | null) {
  const [embedComposerOpen, setEmbedComposerOpen] = useState(false)
  const [embedInputValue, setEmbedInputValue] = useState("")
  const [embedInputError, setEmbedInputError] = useState<string | null>(null)
  const embedComposerRef = useRef<HTMLDivElement | null>(null)
  const embedTextareaRef = useRef<HTMLTextAreaElement | null>(null)

  const openEmbedComposer = useCallback(() => {
    setEmbedInputError(null)
    setEmbedComposerOpen(true)
  }, [])

  const handleCancelEmbed = useCallback(() => {
    setEmbedComposerOpen(false)
    setEmbedInputValue("")
    setEmbedInputError(null)
    if (editor) {
      requestAnimationFrame(() => {
        editor.commands.focus()
      })
    }
  }, [editor])

  const handleSubmitEmbed = useCallback(() => {
    if (!editor) return
    const attrs = parseEmbedInput(embedInputValue)
    if (!attrs) {
      setEmbedInputError("Please paste a valid embed code or URL.")
      return
    }
    editor.chain().focus().setEmbedIframe(attrs).run()
    setEmbedInputValue("")
    setEmbedInputError(null)
    setEmbedComposerOpen(false)
    requestAnimationFrame(() => {
      editor.commands.focus()
    })
  }, [editor, embedInputValue])

  useEffect(() => {
    if (!embedComposerOpen) return
    requestAnimationFrame(() => {
      embedTextareaRef.current?.focus()
    })
  }, [embedComposerOpen])

  useEffect(() => {
    if (!embedComposerOpen || !embedComposerRef.current) return
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as globalThis.Node | null
      if (!target) return
      if (embedComposerRef.current?.contains(target)) return
      handleCancelEmbed()
    }
    document.addEventListener("pointerdown", handlePointerDown)
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
    }
  }, [embedComposerOpen, handleCancelEmbed])

  return {
    embedComposerOpen,
    embedInputValue,
    setEmbedInputValue,
    embedInputError,
    setEmbedInputError,
    embedComposerRef,
    embedTextareaRef,
    handleSubmitEmbed,
    handleCancelEmbed,
    openEmbedComposer
  }
}
